/**
 * Type-aware value resolution — the view-agnostic half of field enrichment.
 * Given a junction field + a record-like object, resolve its target entities
 * (and their ids) off the joined relation; given a reference id, resolve it via
 * the by-id entity stores. WHERE the value lives and what the public key is are
 * the view-specific half, owned by each collection's `enrichField`, which calls
 * these with its own item shape and data key.
 *
 * A leaf module (stores + types only), so the enrichers depend on it without a
 * cycle through the `./index` barrel.
 */

import { getSchema } from "../registries/schemas.js"
import { getType } from "../registries/types.js"
import { getUserById } from "../registries/users.js"
import { withAppearance } from "./appearance.js"
import { type ReferenceFieldType } from "./types.js"

type RowWithId = { id: number | string; [k: string]: unknown }

/** Options accepted by the per-collection enrichers (the two `enrichField`s).
 *  Shared so both collections take the same shape. */
export interface EnrichOptions {
  schema_id?: number
  isEditable?: boolean
  value?: (item: unknown) => unknown
  [k: string]: unknown
}

/** A field's junction rows on `item[relation]` — those whose `schema_key` names
 *  the field, sorted by row `id` (the write-side order, so read and write agree
 *  on chip order). The rows are authoritative for membership; `data[key]` is not
 *  read. */
function junctionRows(item: unknown, relation: string, key: string): RowWithId[] {
  const rows = (item as Record<string, unknown> | null)?.[relation]
  if (!Array.isArray(rows)) return []
  return (rows as (RowWithId & { schema_key?: string })[])
    .filter((row) => row.schema_key === key)
    .sort((a, b) => Number(a.id) - Number(b.id))
}

/** The unqualified rows on `item[relation]` (no `schema_key`) — the rows a
 *  direct field reads. Rows with a `schema_key` belong to a named field
 *  (`junctionRows`). */
function directRows(item: unknown, relation: string): RowWithId[] {
  const rows = (item as Record<string, unknown> | null)?.[relation]
  if (!Array.isArray(rows)) return []
  return (rows as (RowWithId & { schema_key?: string | null })[]).filter(
    (r) => r.schema_key == null
  )
}

/** Record targets carry their icon and colour (see ./appearance); any other target is
 *  returned as it is. `isRecordTarget` is whether the def's targets are records. */
const decorateTargets = (isRecordTarget: boolean) =>
  isRecordTarget ? withAppearance : <T>(target: T): T => target

/** A junction row's target id. Prefers the inner reference's scalar FK `column`
 *  (`linked_id`/`tag_id`/`asset_id`) so an orphaned row (target deleted) still
 *  contributes its id; falls back to the hop's id, then the row's own id (plain
 *  references, where the row IS the entity). */
function junctionTargetId(row: RowWithId, def: ReferenceFieldType): unknown {
  const column = def.reference?.column
  if (column) return row[column]
  const hop = def.reference?.relationship
  if (hop) return (row[hop] as RowWithId | undefined)?.id
  return row.id
}

/**
 * `value` for a junction field: its `schema_key`-scoped rows hopped to their
 * target entities (via the inner `reference.relationship`), in `id` order. An
 * absent hop → `null` (placeholder chip). Null when the def has no `relationship`.
 */
export function resolveJunction(
  def: ReferenceFieldType,
  schemaKey: string
): ((record: unknown) => unknown) | null {
  const relation = def.relationship
  if (!relation) return null
  const targetKey = def.reference?.relationship ?? null
  const decorate = decorateTargets(def.reference?.table === "records")
  return (item: unknown) =>
    junctionRows(item, relation, schemaKey).map((row) =>
      targetKey ? decorate((row[targetKey] as RowWithId | undefined) ?? null) : row
    )
}

/**
 * `rawValue` for a junction field: the target id of each `schema_key`-scoped row
 * (via `junctionTargetId`), aligned with `resolveJunction`'s entities so chips
 * match by id. Reading the scalar FK preserves an orphaned row's id (Unknown-ID
 * chip, not silently dropped → deleted). Null when no `relationship`.
 */
export function resolveJunctionTargetIds(
  def: ReferenceFieldType,
  schemaKey: string
): ((record: unknown) => unknown[]) | null {
  const relation = def.relationship
  if (!relation) return null
  return (item: unknown) =>
    junctionRows(item, relation, schemaKey)
      .map((row) => junctionTargetId(row, def))
      .filter((id) => id != null)
}

/**
 * `value` for a direct field — one whose value is a relation on the record
 * itself, nothing stored in `data`: the aggregate relationship fields
 * (taggings/contacts/…, whose key IS the relation) and `related` fields (a
 * computed relation such as `events`). The unqualified rows (`directRows`),
 * hopped to their target entities for junctions or returned as-is for plain
 * references. Null when the def has no `relationship`.
 */
export function resolveDirect(def: ReferenceFieldType): ((record: unknown) => unknown) | null {
  const relation = def.relationship
  if (!relation) return null
  const hop = def.reference?.relationship ?? null
  const decorate = decorateTargets(def.reference?.table === "records")
  return (item: unknown) => {
    const rows = directRows(item, relation)
    if (!hop) return rows
    return rows.map((row) => decorate((row[hop] as RowWithId | undefined) ?? null))
  }
}

/** `rawValue` counterpart of `resolveDirect`: the target id of each unqualified
 *  row (via `junctionTargetId`), aligned with its entities. Null when the def has
 *  no `relationship`. */
export function resolveDirectIds(def: ReferenceFieldType): ((record: unknown) => unknown[]) | null {
  const relation = def.relationship
  if (!relation) return null
  return (item: unknown) =>
    directRows(item, relation)
      .map((row) => junctionTargetId(row, def))
      .filter((id) => id != null)
}

/**
 * `value` for a plain reference: the stored id resolved to its entity, dispatched
 * on the def — a `relationship` (contact/time/source/parent) looks the value up in
 * that relation on the item; else a by-id store (user/schema/type).
 * `readId` reads the stored id (`data[key]` or a top-level column). `schemaKey`,
 * when given, marks a schema-qualified item relation (contact/time/source):
 * membership is its `schema_key === schemaKey` rows, not `readId`. Null when
 * unresolvable (caller keeps the raw-id accessor).
 */
export function resolveReference(
  def: ReferenceFieldType,
  readId: (record: unknown) => unknown,
  schemaKey?: string
): ((record: unknown) => unknown) | null {
  if (def.relationship) return resolveItemReference(def, readId, schemaKey)
  return resolveStoreReference(def, readId)
}

/** By-id store lookup for `type`/`user`/`schema`; null (with a debug line) for
 *  reference types with no store. */
function resolveStoreReference(
  def: ReferenceFieldType,
  readId: (record: unknown) => unknown
): ((record: unknown) => unknown) | null {
  let resolveId: (id: number | string) => unknown
  switch (def.name) {
    case "type":
      resolveId = (id) => (typeof id === "string" ? getType(id) || null : null)
      break
    case "user":
      resolveId = (id) => (typeof id === "number" ? getUserById(id) : null)
      break
    case "schema":
      resolveId = (id) => (typeof id === "number" ? getSchema(id) || null : null)
      break
    default:
      // No by-id store for this reference type. Expected, not an error: the
      // generic `record` reference stays raw-id by design (types/reference.ts),
      // and the caller keeps the raw-id accessor.
      console.debug(`No store resolver for reference type ${def.name}`)
      return null
  }
  return (item: unknown) => {
    const stored = readId(item)
    if (stored == null) return null
    return Array.isArray(stored)
      ? stored.map((id) => resolveId(id as number | string)).filter(Boolean)
      : resolveId(stored as number | string)
  }
}

/** Resolve a reference against a relation on the item (contact → `record.contacts`,
 *  parent → `record.parent`, …). With `schemaKey` the field is schema-qualified
 *  (contact/time/source): membership is the relation's `schema_key === schemaKey`
 *  rows (the row IS the value), so `data[key]` isn't read — a record whose relation
 *  wasn't selected reads empty (no raw-id fallback), which the record queries load.
 *  Without `schemaKey` (fixed `parent`): `readId` reads the stored id(s), each looked
 *  up in `item[relationship]`; a query gap keeps the raw id and warns. Returns the
 *  matched row(s) as an array for the qualified case (the enricher shapes to the
 *  field's arity). Null when the def has no relation. */
function resolveItemReference(
  def: ReferenceFieldType,
  readId: (record: unknown) => unknown,
  schemaKey?: string
): ((record: unknown) => unknown) | null {
  const relation = def.relationship
  if (!relation) return null
  if (schemaKey != null) return (item: unknown) => junctionRows(item, relation, schemaKey)
  return (item: unknown) => {
    const stored = readId(item)
    // Nothing stored → empty for arrays, null for scalars.
    if (stored == null) return Array.isArray(stored) ? [] : null

    // An array relation, or an object one (`parent`, a single row) as its only
    // row; null is a loaded relation with nothing in it.
    const loaded = (item as Record<string, unknown> | null)?.[relation]
    const rows = Array.isArray(loaded) ? loaded : loaded === null ? [] : loaded ? [loaded] : null
    // Relation not selected by the query — keep the raw id(s) so the cell isn't
    // blanked, and warn (usually a query gap).
    if (!rows) {
      console.warn(`resolveItemReference: relation "${relation}" not loaded for ${def.name} field`)
      return stored
    }

    const rowById = new Map((rows as RowWithId[]).map((row) => [String(row.id), row]))
    const decorate = decorateTargets(def.table === "records")
    // Array of ids → rows in stored order (unmatched → null, preserving index).
    if (Array.isArray(stored)) return stored.map((id) => decorate(rowById.get(String(id)) ?? null))
    // Scalar id → the single row, or the raw id if it didn't match.
    const row = rowById.get(String(stored))
    return row ? decorate(row) : stored
  }
}
